<template>
  <div class="row">
    <div class="col-12">
      <div class="ads-page-header d-md-flex align-items-center justify-content-between mb-20">
        <div class="d-flex align-items-center">
          <span class="ads-page-icon">
            <i class="flaticon-notification"></i>
          </span>
          <div class="ms-12">
            <h4 class="mb-0 fw-bold">Gestion des annonces</h4>
            <p class="mb-0 text-muted fs-13">
              {{ users.total }} annonce{{ users.total > 1 ? "s" : "" }} au total
            </p>
          </div>
        </div>
      </div>

      <div class="card ads-card mb-25 border-0 letter-spacing">
        <div
          class="card-head d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
        >
          <div class="d-sm-flex align-items-center">
            <button
              class="ads-btn-primary position-relative transition border-0 fw-medium text-white d-inline-flex align-items-center me-10 mb-10 mb-lg-0"
              type="button"
              @click="openCreateModal"
              v-if="auth.can('SETTINGS_ADS')"
            >
              <i class="flaticon-plus position-relative fs-12 me-8"></i>
              Nouvelle annonce
            </button>
            <button
              class="ads-btn-outline position-relative transition fw-medium d-inline-flex align-items-center mb-10 mb-lg-0"
              type="button"
            >
              <i class="flaticon-file-1 position-relative fs-15 me-8"></i>
              Export
            </button>
          </div>
          <div class="ads-search position-relative">
            <i class="flaticon-search ads-search-icon"></i>
            <input
              type="text"
              class="form-control ps-40"
              v-model="searchTerm"
              @input="handleInput()"
              placeholder="Rechercher une annonce..."
            />
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25 pt-0">
          <EasyDataTable
            :headers="headers"
            :items="rows"
            :loading="loading"
            :pagination="pagination"
            :theme-color="themeColor"
            alternating
            table-class-name="ads-table"
            @page-change="fetchData"
          >
            <template #loading>
              <div class="skeleton-table w-100">
                <div class="loading-spinner-wrap text-center py-15">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Chargement...</span>
                  </div>
                </div>
                <div
                  class="skeleton-row d-flex align-items-center"
                  v-for="n in 6"
                  :key="n"
                >
                  <div
                    class="skeleton-cell"
                    v-for="c in headers.length"
                    :key="c"
                  >
                    <span class="skeleton-bar"></span>
                  </div>
                </div>
              </div>
            </template>

            <template #empty-message>
              <div class="empty-state text-center py-40">
                <i class="flaticon-notification fs-40 d-block mb-10 text-muted"></i>
                <p class="mb-0 text-muted">Aucune annonce trouvée</p>
              </div>
            </template>

            <template #item-name="{ name }">
              <span class="fw-medium ads-name-cell">{{ name }}</span>
            </template>

            <template #item-ads_statut="{ id, ads_statut }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-' + id"
                  :checked="ads_statut"
                  @change="toggleAdActive(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-' + id"></label>
              </div>
            </template>

            <template #item-ads_type="{ ads_type }">
              <span
                class="status-pill"
                :class="{
                  'status-pill-success': ads_type === 'success',
                  'status-pill-danger': ads_type === 'danger',
                  'status-pill-warning': ads_type === 'warning',
                }"
              >
                <span class="status-pill-dot"></span>
                {{ ads_type }}
              </span>
            </template>

            <template #item-ads_for="{ ads_for }">
              <span
                class="status-pill"
                :class="{
                  'status-pill-primary': ads_for === 'ALL',
                  'status-pill-info': ads_for === 'CSTMR',
                }"
              >
                {{ ads_for === "ALL" ? "Annonce Globale" : "Pour des clients" }}
              </span>
            </template>

            <template #item-created_at="{ created_at }">
              <span class="text-muted fs-13">{{ getDate(created_at) }}</span>
            </template>
            <template #item-actions="{ id }">
              <div class="d-flex align-items-center gap-8" v-if="auth.can('SETTINGS_ADS')">
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-edit"
                  title="Modifier"
                  @click="openEditModal(id)"
                >
                  <i class="flaticon-pen"></i>
                </button>
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-delete"
                  title="Supprimer"
                  @click="deleteAds(id)"
                >
                  <i class="flaticon-delete"></i>
                </button>
              </div>
            </template>
          </EasyDataTable>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <div
    class="modal fade"
    id="createnewAdsModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content ads-modal-content">
        <!-- Modal Header -->
        <div class="modal-header ads-modal-header">
          <div class="d-flex align-items-center">
            <span class="ads-modal-icon">
              <i class="flaticon-notification"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">
              {{ isEditing ? "Modifier l'annonce" : "Nouvelle annonce" }}
            </h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <form @submit.prevent="submitAd">
            <!-- Ad Name -->
            <div class="mb-20">
              <label class="form-label fw-medium">Libellé de l'annonce</label>
              <input
                type="text"
                v-model="newAd.name"
                class="form-control"
                placeholder="Ex: Promotion de fin d'année"
                required
              />
            </div>

            <!-- Ad Period -->
            <div class="mb-20">
              <label class="form-label fw-medium">Période de l'annonce</label>
              <div class="row g-10 align-items-center">
                <div class="col-sm">
                  <input
                    type="date"
                    v-model="newAd.from_date"
                    class="form-control"
                    required
                  />
                </div>
                <div class="col-auto text-muted d-none d-sm-block">au</div>
                <div class="col-sm">
                  <input
                    type="date"
                    v-model="newAd.to_date"
                    class="form-control"
                    :min="newAd.from_date"
                    required
                  />
                </div>
              </div>
            </div>

            <div class="row g-10">
              <!-- Ad Type -->
              <div class="col-md-6 mb-20">
                <label class="form-label fw-medium">Type Annonce</label>
                <select class="form-select" v-model="newAd.type_ads" required>
                  <option value="" disabled>-- Sélectionner --</option>
                  <option
                    v-for="option in typesAds"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>

              <!-- Ad For -->
              <div class="col-md-6 mb-20">
                <label class="form-label fw-medium">Annonce Pour</label>
                <select class="form-select" v-model="newAd.for_ads" required>
                  <option value="" disabled>-- Sélectionner --</option>
                  <option
                    v-for="option in forList"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Customers (Conditional) -->
            <div v-if="newAd.for_ads === 'CSTMR'" class="mb-20">
              <label class="form-label fw-medium">Clients</label>
              <v-select
                class="custom-select-height"
                label="name"
                v-model="newAd.customers"
                :options="customerList"
                :reduce="(c) => c.id"
                multiple
              />
            </div>

            <!-- Content -->
            <div class="mb-3">
              <label class="form-label fw-medium">Contenu de l'annonce</label>
              <QuillEditor
                v-model:content="newAd.content"
                content-type="html"
                theme="snow"
                placeholder="Écrire le contenu de l'annonce"
              />
            </div>
            <!-- :modules="editorModules" -->
          </form>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer ads-modal-footer">
          <button
            type="button"
            class="ads-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Fermer
          </button>
          <button type="button" @click="submitAd" class="ads-btn-primary border-0 transition fw-medium">
            {{ isEditing ? "Mettre à jour" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Modal } from "bootstrap";
import moment from "moment";
import { onMounted, reactive, ref } from "vue";
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { useAppToast } from "@/composables/useAppToast";

export default {
  name: "AdsContent",
  setup: () => {
        const { showToast } = useAppToast();

    const newAd = reactive({
      name: "",
      from_date: "",
      to_date: "",
      type_ads: "",
      for_ads: "",
      customers: [],
      content: "",
    });
    const isEditing = ref(false);
    const headers = [
      {
        text: "Libellé",
        value: "name",
      },
      {
        text: "Annonce Pour",
        value: "ads_for",
      },
      {
        text: "Type",
        value: "ads_type",
      },
      {
        text: "Date début",
        value: "ads_from",
      },
      {
        text: "Date Fin",
        value: "ads_to",
      },
      {
        text: "Status",
        value: "ads_statut",
      },
      {
        text: "Date d'ajout",
        value: "created_at",
      },
      {
        text: "Actions",
        value: "actions",
      },
    ];

    const rows = ref([]);
    const loading = ref(true);
    const themeColor = "#06b48a";

    const users = reactive({
      searchTerm: "",
      searchObject: "",
      searchStatut: "",
      searchSDate: "",
      searchEDate: "",
      current_page: 1,
      per_page: 10,
      total: 0,
    });

    const searchTerm = ref("");
    const status_filter = ref("");
    const object_filter = ref("");
    const date_filter = ref("");

    const forList = ref([
      { name: "Afficher pour tous", id: "ALL" },
      { name: "Choisir Clients", id: "CSTMR" },
    ]);
    const typesAds = ref([
      { name: "succès", id: "success" },
      { name: "danger", id: "danger" },
      { name: "warning", id: "warning" },
    ]);

    const auth = useAuthStore();
    const user = auth.user;

    const customerList = ref([]);
    // Pagination object for EasyDataTable
    const pagination = reactive({
      page: 1,
      perPage: 10,
      total: 0,
    });

    // Fetch data from API
    const fetchData = async (page = 1) => {
      loading.value = true;
      users.current_page = page;

      try {
        const res = await api.get(`/api/auth/list_ads`, {
          params: {
            searchTerm: users.searchTerm,
            searchObject: users.searchObject,
            searchStatut: users.searchStatut,
            searchSDate: users.searchSDate,
            searchEDate: users.searchEDate,
            page: users.current_page,
            per_page: users.per_page,
          },
        });

        rows.value = res.data.users.data;
        users.total = res.data.users.total;

        // Update pagination for EasyDataTable
        pagination.total = res.data.users.total;
        pagination.page = res.data.users.current_page;
        pagination.perPage = res.data.users.per_page;
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    let searchDebounce = null;
    function handleInput() {
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(() => {
        users.searchTerm = searchTerm.value;
        fetchData(1);
      }, 400);
    }

    onMounted(async () => {
      fetchData();
      try {
        const response = await api.get("/api/auth/Customers");
        if (response && response.data) {
          // handle the fetched rows data
          // console.log("Fetched rows:", response.data);
          customerList.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching rows:", error);
      }
    });

    function resetForm() {
      Object.assign(newAd, {
        id: null,
        name: "",
        from_date: "",
        to_date: "",
        type_ads: "",
        for_ads: "",
        customers: [],
        content: "",
      });
    }

    function openCreateModal() {
      isEditing.value = false;
      resetForm();

      const modalEl = document.getElementById("createnewAdsModal");
      const modal = new Modal(modalEl);
      modal.show();
    }

    function openEditModal(id) {
      const rowAd = rows.value.find((c) => c.id === id);
      if (rowAd) {
        console.log(rowAd);
        isEditing.value = true;
        Object.assign(newAd, {
          id: rowAd.id,
          name: rowAd.name,
          from_date: rowAd.ads_from
            ? moment(rowAd.ads_from).format("YYYY-MM-DD")
            : "",
          to_date: rowAd.ads_to
            ? moment(rowAd.ads_to).format("YYYY-MM-DD")
            : "",
          type_ads: rowAd.ads_type,
          for_ads: rowAd.ads_for,
          customers: (rowAd.ads_clients || []).map((ac) => ac.client_id),
          content: rowAd.ads_text || "",
        });

        const modalEl = document.getElementById("createnewAdsModal");
        const modal = new Modal(modalEl);
        modal.show();
      }
    }

    async function submitAd() {
      if (!newAd.name) {
        showToast("Please fill in all fields!", "error");
        return;
      }

      if (isEditing.value) {
        try {
          const response = await api.post("/api/auth/edit_ads/" + newAd.id, {
            name: newAd.name,
            from_date: newAd.from_date,
            to_date: newAd.to_date,
            type_ads: newAd.type_ads,
            for_ads: newAd.for_ads,
            customers: newAd.customers,
            content: newAd.content,
          });

          if (response.data.success || response.data.msg == "success") {
            const modalEl = document.getElementById("createnewAdsModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            fetchData();
            showToast("Annonce mise à jour avec succès!", "success");
          } else {
            showToast("Erreur: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while editing the ad.", "error");
        }
      } else {
        try {
          // Example: send to API
          const response = await api.post("/api/auth/add_ads", {
            name: newAd.name,
            from_date: newAd.from_date,
            to_date: newAd.to_date,
            type_ads: newAd.type_ads,
            for_ads: newAd.for_ads,
            customers: newAd.customers,
            content: newAd.content,
            user_id: user?.id,
          });

          if (response.data.success || response.data.msg == "success") {
            const modalEl = document.getElementById("createnewAdsModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            // get rows list refresh
            fetchData();
            showToast("Zone added successfully!", "success");
          } else {
            showToast("Error: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while adding the city.", "error");
        }
      }
    }

    async function deleteAds(id) {
      if (confirm("Are you sure you want to delete this ad?")) {
        try {
          const response = await api.post(`/api/auth/remove_ads/${id}`);
          if (response.data.message === "success") {
            // Remove the city from the local list
            fetchData();
            showToast("City deleted successfully!", "success");
          } else {
            showToast("Error deleting city: " + response.data.message, "error");
          }
        } catch (error) {
          console.error("Error deleting city:", error);
          showToast("An error occurred while deleting the city.", "error");
        }
      }
    }
    async function toggleAdActive(id, value) {
      const item = rows.value.find((z) => z.id === id);
      if (item) {
        item.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/active_ads/${id}`, {
          active: value,
        });
        console.log("Zone active status updated:", res.data);
      }
    }
    function getDate(value) {
      return moment.utc(String(value)).format("YYYY-MM-DD HH:mm");
    }

    return {
      newAd,
      submitAd,
      headers,
      rows,
      openCreateModal,
      openEditModal,
      isEditing,
      deleteAds,
      typesAds,
      forList,
      customerList,
      getDate,
      toggleAdActive,
      auth,
      themeColor,
      searchTerm,
      handleInput,
      users,
      pagination,
      loading,
    };
  },
};
</script>

<style scoped>
/* ---------- Page header ---------- */
.ads-page-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  font-size: 20px;
}

/* ---------- Card ---------- */
.ads-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(43, 42, 63, 0.06);
}

.ads-card .card-head {
  border-bottom: 1px solid #f0f0f5;
}

/* ---------- Buttons ---------- */
.ads-btn-primary {
  background: #06b48a;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-primary:hover {
  background: #059c78;
  color: #fff;
}

.ads-btn-outline {
  background: transparent;
  border: 1px solid #e2e2ea;
  color: #2b2a3f;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-outline:hover {
  background: #f8f8fa;
  border-color: #d5d5e0;
}

/* ---------- Search ---------- */
.ads-search {
  min-width: 260px;
}

.ads-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a1bb;
  font-size: 14px;
}

.ads-search .form-control {
  border-radius: 8px;
  border: 1px solid #e2e2ea;
  height: 44px;
}

.ads-search .form-control:focus {
  border-color: #06b48a;
  box-shadow: 0 0 0 3px rgba(6, 180, 138, 0.12);
}

/* ---------- Table ---------- */
.ads-name-cell {
  color: #2b2a3f;
}

:deep(.ads-table) {
  --easy-table-border: none;
  --easy-table-row-border: 1px solid #f1f1f5;

  --easy-table-header-font-size: 12px;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #8e8da1;
  --easy-table-header-background-color: #f8f8fb;
  --easy-table-header-item-padding: 12px 16px;

  --easy-table-body-row-font-size: 14px;
  --easy-table-body-row-height: 60px;
  --easy-table-body-row-font-color: #2b2a3f;
  --easy-table-body-row-background-color: #ffffff;
  --easy-table-body-item-padding: 12px 16px;

  --easy-table-body-row-hover-font-color: #2b2a3f;
  --easy-table-body-row-hover-background-color: #f8fbfa;

  --easy-table-body-even-row-font-color: #2b2a3f;
  --easy-table-body-even-row-background-color: #fbfbfd;

  --easy-table-footer-font-color: #8e8da1;
  --easy-table-footer-font-size: 13px;
  --easy-table-footer-background-color: #ffffff;

  --easy-table-message-font-color: #8e8da1;

  --easy-table-loading-mask-background-color: #ffffff;
}

:deep(.ads-table th) {
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ---------- Status pills ---------- */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.status-pill-success {
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.status-pill-danger {
  background: rgba(239, 41, 41, 0.1);
  color: #ef2929;
}

.status-pill-warning {
  background: rgba(243, 196, 76, 0.18);
  color: #c9932a;
}

.status-pill-primary {
  background: rgba(101, 96, 240, 0.1);
  color: #6560f0;
}

.status-pill-info {
  background: rgba(31, 177, 230, 0.1);
  color: #1fb1e6;
}

/* ---------- Action icon buttons ---------- */
.ads-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #eceef1;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8e8da1;
  transition: all 0.2s ease;
}

.ads-icon-btn-edit:hover {
  background: rgba(101, 96, 240, 0.1);
  border-color: transparent;
  color: #6560f0;
}

.ads-icon-btn-delete:hover {
  background: rgba(239, 41, 41, 0.1);
  border-color: transparent;
  color: #ef2929;
}

/* ---------- Modal ---------- */
.ads-modal-content {
  border: none;
  border-radius: 14px;
  overflow: hidden;
}

.ads-modal-header {
  background: #f8f8fb;
  border-bottom: 1px solid #f0f0f5;
  padding: 18px 25px;
}

.ads-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.ads-modal-footer {
  border-top: 1px solid #f0f0f5;
  padding: 16px 25px;
}

.ads-modal-footer .ads-btn-primary,
.ads-modal-footer .ads-btn-outline {
  padding: 9px 20px;
}

/* ---------- Loading skeleton ---------- */
.skeleton-table {
  padding: 10px 0;
}

.skeleton-row {
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f1;
  gap: 20px;
}

.skeleton-cell {
  flex: 1;
}

.skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e5e9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.empty-state {
  width: 100%;
}
</style>